// components/AuthButton.tsx - Simplified auth button (Google + Email)
import React, { useState, useRef, useEffect } from 'react';
import { LogIn, LogOut, Crown, ShieldAlert } from 'lucide-react';
import { UserProfile } from './services/authService';
import AuthModal from './AuthModal';
import { FREE_TIER_MAX_VIDEOS } from '../constants';
import { SHOW_UPLOAD_SLOT_LIMIT_UI } from './services/uploadQuotaPolicy';

const SEGMENT_BUTTON_COLOR = '#6C609F';
const TOP_ACTION_BG_COLOR = '#eef2f7';
const TOP_ACTION_BG_HOVER_COLOR = '#e7edf5';
const TOP_ACTION_BORDER_COLOR = '#dbe1ea';

interface AuthButtonProps {
  isAuthenticated: boolean;
  userProfile: UserProfile | null;
  onSignInGoogle: () => Promise<void>;
  onSignInEmail: (email: string, password: string) => Promise<void>;
  onSignUpEmail: (email: string, password: string, name: string) => Promise<void>;
  onResetPassword: (email: string) => Promise<void>;
  onSignOut: () => Promise<void>;
  onDeleteAccount?: () => Promise<void>;
  onShowPurchaseModal?: () => void;
  isFullscreen?: boolean;
}

const AuthButton: React.FC<AuthButtonProps> = ({
  isAuthenticated,
  userProfile,
  onSignInGoogle,
  onSignInEmail,
  onSignUpEmail,
  onResetPassword,
  onSignOut,
  onDeleteAccount,
  onShowPurchaseModal,
  isFullscreen = false
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPhotoFailed(false);
  }, [userProfile?.photoURL]);

  // Derived limits with sensible defaults
  const libraryLimit = userProfile?.libraryLimit ?? FREE_TIER_MAX_VIDEOS;
  const libraryCount = userProfile?.libraryCount ?? 0;
  const remaining = Math.max(libraryLimit - libraryCount, 0);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
        setConfirmingDelete(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      await onSignOut();
      setShowMenu(false);
    } catch (error) {
      alert('Failed to sign out. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowPurchase = () => {
    if (onShowPurchaseModal) {
      onShowPurchaseModal();
    }
  };

  const handleDeleteAccount = async () => {
    if (!onDeleteAccount) return;
    setIsDeleting(true);
    try {
      await onDeleteAccount();
      setShowMenu(false);
      setConfirmingDelete(false);
    } catch (error) {
      alert('Failed to delete account. Please try again or contact support.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <button
          onClick={() => setShowAuthModal(true)}
          title="Sign in"
          aria-label="Sign in"
          style={{
            width: '44px',
            minWidth: '44px',
            maxWidth: '44px',
            padding: '0.5rem',
            backgroundColor: isFullscreen ? '#1e293b' : TOP_ACTION_BG_COLOR,
            color: isFullscreen ? '#e2e8f0' : SEGMENT_BUTTON_COLOR,
            borderRadius: '0.5rem',
            border: isFullscreen ? '1px solid #334155' : `1px solid ${TOP_ACTION_BORDER_COLOR}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0,
            minHeight: '36px',
            fontSize: '0.85rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            boxShadow: '0 6px 14px rgba(15, 23, 42, 0.14)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = isFullscreen ? '#334155' : TOP_ACTION_BG_HOVER_COLOR;
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = isFullscreen ? '#1e293b' : TOP_ACTION_BG_COLOR;
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          }}
        >
          <LogIn size={14} />
        </button>

        {showAuthModal && (
          <AuthModal
            onClose={() => setShowAuthModal(false)}
            onSignInGoogle={onSignInGoogle}
            onSignInEmail={onSignInEmail}
            onSignUpEmail={onSignUpEmail}
            onResetPassword={onResetPassword}
          />
        )}
      </>
    );
  }

  return (
    <div style={{ position: 'relative' }} ref={menuRef}>
      <button
        onClick={() => setShowMenu(!showMenu)}
        style={{
          padding: '0.5rem',
          backgroundColor: isFullscreen ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6',
          color: isFullscreen ? 'white' : '#374151',
          borderRadius: '0.5rem',
          border: isFullscreen ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #e5e7eb',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s'
        }}
        title={userProfile?.displayName || 'User Menu'}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = isFullscreen ? 'rgba(255, 255, 255, 0.2)' : '#e5e7eb';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = isFullscreen ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6';
        }}
      >
        {userProfile?.photoURL && !photoFailed ? (
          <img
            src={userProfile.photoURL}
            alt="Profile"
            referrerPolicy="no-referrer"
            onError={() => setPhotoFailed(true)}
            style={{
              width: '2rem',
              height: '2rem',
              borderRadius: '50%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <div style={{
            width: '2rem',
            height: '2rem',
            borderRadius: '50%',
            backgroundColor: '#2563eb',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            fontSize: '0.875rem'
          }}>
            {userProfile?.displayName?.charAt(0).toUpperCase() || 'U'}
          </div>
        )}
      </button>

      {showMenu && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '0.5rem',
            backgroundColor: isFullscreen ? 'rgba(0, 0, 0, 0.95)' : 'white',
            color: isFullscreen ? 'white' : 'black',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            border: isFullscreen ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #e5e7eb',
            minWidth: '280px',
            zIndex: 50,
            overflow: 'hidden'
          }}
        >
          {/* User Info */}
          <div style={{
            padding: '1.25rem',
            borderBottom: isFullscreen ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.9375rem' }}>
              {userProfile?.displayName}
            </div>
            <div style={{ fontSize: '0.8125rem', opacity: 0.7 }}>
              {userProfile?.email}
            </div>
          </div>

          
    {/* Library Info — slot counter hidden while uploads are free; premium badge stays. */}
    {(SHOW_UPLOAD_SLOT_LIMIT_UI || userProfile?.isPremium) && (
    <div style={{
      padding: '1.25rem',
      borderBottom: isFullscreen ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb'
    }}>
      {userProfile?.isPremium ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem',
          backgroundColor: 'rgba(251, 191, 36, 0.1)',
          borderRadius: '0.5rem',
          border: '1px solid rgba(251, 191, 36, 0.3)'
        }}>
          <Crown size={18} color="#fbbf24" />
          <div>
            <div style={{ color: '#fbbf24', fontWeight: '600', fontSize: '0.875rem' }}>
              Premium User
            </div>
            <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.125rem' }}>
              Purchased video storage slots.
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '0.75rem'
          }}>
            <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Storage available</span>
            <span style={{ 
              fontSize: '0.75rem', 
              padding: '0.25rem 0.5rem',
              backgroundColor: (userProfile?.libraryLimit || 0) === 0 ? '#fee2e2' : '#dbeafe',
              color: (userProfile?.libraryLimit || 0) === 0 ? '#991b1b' : '#1e40af',
              borderRadius: '0.25rem',
              fontWeight: '600'
            }}>
              {libraryCount} / {libraryLimit} used
            </span>
          </div>
          
          {/* Progress bar */}
          <div style={{
            width: '100%',
            height: '6px',
            backgroundColor: isFullscreen ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb',
            borderRadius: '3px',
            overflow: 'hidden',
            marginBottom: '0.5rem'
          }}>
            <div style={{
              height: '100%',
              width: `${Math.min((libraryCount / libraryLimit) * 100, 100)}%`,
              backgroundColor: libraryCount < Math.ceil(libraryLimit * 0.6) / 2 ? '#10b981' : 
                            libraryCount < libraryLimit ? '#f59e0b' : '#ef4444',
              transition: 'width 0.3s ease'
            }} />
          </div>
          
          <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
            {remaining} slot{remaining !== 1 ? 's' : ''} remaining
          </div>
          
          {onShowPurchaseModal && (
            <>
              <div style={{ 
                fontSize: '0.75rem', 
                color: '#dc2626', 
                marginTop: '0.5rem',
                fontWeight: '500'
              }}>
                
              </div>
              <button
                onClick={() => {
                        setShowMenu(false);
                        handleShowPurchase();
                      }}
                style={{
                  width: '100%',
                  marginTop: '0.75rem',
                  padding: '0.325rem',
                  backgroundColor: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: '0.9125rem',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6d28d9'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.95rem' }}>
                  <Crown size={14} /> Get More Storage
                </div>
              </button>
            </>
          )}
        </div>
      )}
    </div>
    )}

          {/* Actions */}
          <div style={{ padding: '0.5rem' }}>
            <button
              onClick={handleSignOut}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'transparent',
                color: isFullscreen ? 'white' : '#dc2626',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                opacity: isLoading ? 0.6 : 1,
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = isFullscreen ? 'rgba(255, 255, 255, 0.1)' : '#fee2e2';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <LogOut size={16} />
              {isLoading ? 'Signing out...' : 'Sign Out'}
            </button>
          </div>

          {/* Privacy + account deletion (GDPR) */}
          <div style={{
            padding: '0.5rem',
            borderTop: isFullscreen ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb'
          }}>
            <a
              href="/privacy"
              onClick={() => setShowMenu(false)}
              style={{
                display: 'block',
                width: '100%',
                padding: '0.6rem 0.75rem',
                color: isFullscreen ? 'rgba(255, 255, 255, 0.75)' : '#6b7280',
                fontSize: '0.8125rem',
                textAlign: 'center',
                textDecoration: 'none'
              }}
            >
              Privacy Policy
            </a>

            {onDeleteAccount && !confirmingDelete && (
              <button
                onClick={() => setConfirmingDelete(true)}
                style={{
                  width: '100%',
                  padding: '0.6rem 0.75rem',
                  backgroundColor: 'transparent',
                  color: isFullscreen ? 'rgba(255, 255, 255, 0.6)' : '#9ca3af',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  textAlign: 'center'
                }}
              >
                Delete my account
              </button>
            )}

            {onDeleteAccount && confirmingDelete && (
              <div style={{
                marginTop: '0.25rem',
                padding: '0.75rem',
                backgroundColor: isFullscreen ? 'rgba(220, 38, 38, 0.12)' : '#fee2e2',
                borderRadius: '0.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                  marginBottom: '0.6rem',
                  color: isFullscreen ? '#fecaca' : '#991b1b',
                  fontSize: '0.75rem',
                  lineHeight: 1.4
                }}>
                  <ShieldAlert size={16} style={{ flexShrink: 0, marginTop: '0.05rem' }} />
                  <span>
                    This permanently deletes your account, videos and library data.
                    This cannot be undone.
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setConfirmingDelete(false)}
                    disabled={isDeleting}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      backgroundColor: 'transparent',
                      color: isFullscreen ? 'white' : '#374151',
                      border: isFullscreen ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      cursor: isDeleting ? 'not-allowed' : 'pointer',
                      fontSize: '0.8125rem',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={isDeleting}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: isDeleting ? 'not-allowed' : 'pointer',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                      opacity: isDeleting ? 0.7 : 1
                    }}
                  >
                    {isDeleting ? 'Deleting...' : 'Yes, delete'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthButton;
